import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchTextBoxTransformItem } from '@/lib/text/types/batch-text-box-transform-item'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import type { UnifiedSelectionResizeCommitInitial } from '../types/unified-selection-resize-commit-initial'
import type { UnifiedSelectionTextResizeCommitInitial } from '../types/unified-selection-text-resize-commit-initial'

export function buildUnifiedSelectionShapeResizeCommitItems(params: {
  initials: Record<string, UnifiedSelectionResizeCommitInitial>
  shapes: ShapeData[]
  positionEpsilon?: number
  sizeEpsilon?: number
  rotationEpsilon?: number
  includeRotation?: boolean
}): BatchShapeTransformItem[] {
  const {
    initials,
    shapes,
    positionEpsilon = 1e-4,
    sizeEpsilon = 1e-4,
    rotationEpsilon = 1e-2,
    includeRotation = false,
  } = params
  const items: BatchShapeTransformItem[] = []

  for (const [id, init] of Object.entries(initials)) {
    const shape = shapes.find((candidate) => candidate.id === id)

    if (!shape) continue

    const before: Partial<ShapeData> = {
      x: init.x,
      y: init.y,
      width: init.width,
      height: init.height,
      ...(includeRotation ? { rotation: init.rotation ?? 0 } : {}),
    }
    const after: Partial<ShapeData> = {
      x: shape.x,
      y: shape.y,
      width: shape.width,
      height: shape.height,
      ...(includeRotation ? { rotation: shape.rotation ?? 0 } : {}),
    }
    const changedPosition =
      Math.abs((after.x ?? 0) - (before.x ?? 0)) > positionEpsilon ||
      Math.abs((after.y ?? 0) - (before.y ?? 0)) > positionEpsilon
    const changedSize =
      Math.abs((after.width ?? 0) - (before.width ?? 0)) > sizeEpsilon ||
      Math.abs((after.height ?? 0) - (before.height ?? 0)) > sizeEpsilon
    const changedRotation =
      includeRotation && Math.abs((after.rotation ?? 0) - (before.rotation ?? 0)) > rotationEpsilon

    if (!changedPosition && !changedSize && !changedRotation) continue

    items.push({
      shapeId: id,
      shape,
      before,
      after,
    })
  }

  return items
}

export function buildUnifiedSelectionTextResizeCommitItems(params: {
  initials: Record<string, UnifiedSelectionTextResizeCommitInitial>
  textBoxes: TextBoxData[]
  positionEpsilon?: number
  sizeEpsilon?: number
}): BatchTextBoxTransformItem[] {
  const { initials, textBoxes, positionEpsilon = 1e-4, sizeEpsilon = 1e-4 } = params
  const items: BatchTextBoxTransformItem[] = []

  for (const [id, init] of Object.entries(initials)) {
    const textBox = textBoxes.find((candidate) => candidate.id === id)

    if (!textBox) continue

    const before = {
      x: init.x,
      y: init.y,
      width: init.width,
      height: init.height,
    }
    const after = {
      x: textBox.x,
      y: textBox.y,
      width: textBox.width,
      height: textBox.height,
    }
    const changedPosition =
      Math.abs(after.x - before.x) > positionEpsilon ||
      Math.abs(after.y - before.y) > positionEpsilon
    const changedSize =
      Math.abs(after.width - before.width) > sizeEpsilon ||
      Math.abs(after.height - before.height) > sizeEpsilon

    if (!changedPosition && !changedSize) continue

    items.push({
      textBoxId: id,
      before,
      after,
    })
  }

  return items
}
